import React from 'react';

/**
 * One row of a list, as it reads on a phone.
 *
 * A table is the wrong instrument here: on a 390px screen the reports table is
 * 722px wide, so the status and every action sit off-screen to the right while
 * each row still pays their height. A card carries the same row with nothing
 * cropped.
 *
 * The hierarchy is built for the field rather than the desk — on site you need
 * to know which dossier this is and what state it is in, then get into it.
 * The whole card opens the item; the buttons are the secondary routes, and the
 * one that cannot be undone is an icon set apart from them, which is also the
 * hardest thing to hit by accident with a thumb.
 */
export default function ListCard({
  status,
  accent,
  title,
  reference,
  badge,
  tags,
  meta = [],
  secondary,
  actions,
  destructive,
  onClick,
}) {
  const parts = meta.filter(Boolean);

  return (
    <div
      className={`list-card ${status ? `list-card-${status}` : ''}`}
      style={accent ? { borderLeftColor: accent } : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      <div className="list-card-head">
        <span className="list-card-title">{title}</span>
        <div className="list-card-head-right">
          {badge}
          {/* Kept out of the action row on purpose: it holds the same corner on
              every card however many actions there are, and it is the least
              reachable control rather than one that shifts under a thumb. */}
          {destructive && (
            <span className="list-card-danger" onClick={(e) => e.stopPropagation()}>
              {destructive}
            </span>
          )}
        </div>
      </div>

      {(reference || parts.length > 0) && (
        <div className="list-card-meta">
          {reference && <span className="list-card-ref">{reference}</span>}
          {parts.map((part, i) => (
            <React.Fragment key={i}>
              {(i > 0 || reference) && <span className="list-card-sep">·</span>}
              <span>{part}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {secondary && <div className="list-card-secondary">{secondary}</div>}

      {/* Labels that can be many or long (confirmations…) get a row of their
          own that wraps: in the head they would push the card past the screen. */}
      {tags && <div className="list-card-tags">{tags}</div>}

      {actions && (
        <div className="list-card-actions" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </div>
  );
}
